import { useState, useRef } from 'react';
import Avatar from './Avatar';

// Reusable @mention textarea. Detects "@" tokens, suggests active people, and
// reports the set of mentioned person-ids back via onChange(text, mentionIds).
// Generic — used by the Actions workflow and reusable anywhere comments live.
export default function MentionInput({ value, onChange, people = [], placeholder, rows = 2, autoFocus = false, onSubmit }) {
  const ref = useRef(null);
  const [menu, setMenu] = useState(null); // { query, start } | null
  const [active, setActive] = useState(0);

  // Active staff only for suggestions, but match against everyone for highlighting.
  const suggestPool = people.filter(p => !p.employee_status || p.employee_status === 'active');

  // Names explicitly picked from the suggestion menu → their exact id. This is
  // the source of truth: we must NOT re-derive a picked mention's id purely by
  // name, or a duplicate/legacy person record with the same name (e.g. an old
  // inactive "Jim Booth" alongside the live account) could capture the mention.
  // That mis-id makes the action show under the name in oversight but never
  // reach the real person's My Actions (which joins on their login id).
  const [chosen, setChosen] = useState({}); // "first last" (lower) -> id
  const nameKey = (p) => `${p.first_name} ${p.last_name}`.trim().toLowerCase();

  // Resolve a mentioned name to a person id: prefer the explicitly-chosen id,
  // then an ACTIVE account of that name (matching what the menu suggests), then
  // any record. Guards against namesake/duplicate records.
  const resolveName = (nm, map) => {
    const key = nm.toLowerCase();
    if (map[key]) return map[key];
    const matches = people.filter(p => nameKey(p) === key);
    if (!matches.length) return null;
    const active = matches.find(p => !p.employee_status || p.employee_status === 'active');
    return (active || matches[0]).id;
  };

  const recomputeMentions = (text, map = chosen) => {
    const ids = [];
    // Match longest names first and consume each hit from a working copy, so a
    // shorter name that's a prefix of a longer one ("@Jack" inside "@Jack Gibson")
    // — or a duplicate person record with the same name — can't match twice.
    let working = text;
    const seen = new Set();
    const byLength = [...people].sort((a, b) =>
      `${b.first_name} ${b.last_name}`.trim().length - `${a.first_name} ${a.last_name}`.trim().length);
    for (const p of byLength) {
      const nm = `${p.first_name} ${p.last_name}`.trim();
      const full = `@${nm}`;
      if (nm.length > 0 && !seen.has(nm.toLowerCase()) && working.includes(full)) {
        seen.add(nm.toLowerCase());
        const id = resolveName(nm, map);
        if (id) ids.push(id);
        working = working.split(full).join(' '); // remove so prefixes/dupes don't re-match
      }
    }
    return [...new Set(ids)];
  };

  const handleChange = (e) => {
    const text = e.target.value;
    const caret = e.target.selectionStart;
    const before = text.slice(0, caret);
    const m = before.match(/@([^@\n]{0,30})$/);
    if (m) { setMenu({ query: m[1], start: caret - m[1].length - 1 }); setActive(0); }
    else setMenu(null);
    onChange(text, recomputeMentions(text));
  };

  const suggestions = menu
    ? suggestPool.filter(p => `${p.first_name} ${p.last_name}`.toLowerCase().includes(menu.query.toLowerCase())).slice(0, 6)
    : [];

  const choose = (p) => {
    const text = value || '';
    const insert = `@${p.first_name} ${p.last_name} `;
    const caret = ref.current?.selectionStart ?? text.length;
    const newText = text.slice(0, menu.start) + insert + text.slice(caret);
    // Remember the exact person picked so their id is used verbatim, not
    // re-guessed from the name (which duplicates would make ambiguous).
    const nextChosen = { ...chosen, [nameKey(p)]: p.id };
    setChosen(nextChosen);
    setMenu(null);
    onChange(newText, recomputeMentions(newText, nextChosen));
    requestAnimationFrame(() => {
      if (ref.current) {
        const pos = menu.start + insert.length;
        ref.current.focus();
        ref.current.setSelectionRange(pos, pos);
      }
    });
  };

  const onKeyDown = (e) => {
    if (menu && suggestions.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(a + 1, suggestions.length - 1)); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); choose(suggestions[active]); return; }
      if (e.key === 'Escape')    { setMenu(null); return; }
    }
    // Cmd/Ctrl+Enter submits, when a handler is provided
    if (onSubmit && (e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); onSubmit(); }
  };

  return (
    <div className="relative">
      <textarea
        ref={ref}
        className="form-textarea w-full"
        rows={rows}
        placeholder={placeholder}
        value={value}
        autoFocus={autoFocus}
        onChange={handleChange}
        onKeyDown={onKeyDown}
        onBlur={() => setTimeout(() => setMenu(null), 150)}
      />
      {menu && suggestions.length > 0 && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-44 overflow-y-auto">
          <div className="px-2 py-1 text-[7px] font-bold uppercase tracking-wide text-gray-400 border-b border-gray-100">Tag a person</div>
          {suggestions.map((p, i) => (
            <button
              key={p.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); choose(p); }}
              className={`w-full text-left px-2.5 py-1.5 text-[10px] flex items-center gap-2 ${i === active ? 'bg-navy text-white' : 'hover:bg-gray-50'}`}
            >
              <Avatar as="span" personId={p.id} className="w-4 h-4 rounded-full flex items-center justify-center text-[7px] font-bold text-white flex-shrink-0" style={{ background: p.avatar_color || '#64748b' }}>
                {(p.first_name?.[0] || '') + (p.last_name?.[0] || '')}
              </Avatar>
              <span className="truncate">{p.first_name} {p.last_name}</span>
              {p.designation && <span className={`ml-auto text-[8px] flex-shrink-0 ${i === active ? 'text-white/70' : 'text-gray-400'}`}>{p.designation}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Render comment/message text with @mentions of known people highlighted.
export function renderWithMentions(text, people = []) {
  if (!text) return null;
  const names = people
    .map(p => `${p.first_name} ${p.last_name}`.trim())
    .filter(Boolean)
    .sort((a, b) => b.length - a.length); // longest first so "John Smith" wins over "John"
  if (names.length === 0) return text;
  const escaped = names.map(n => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const re = new RegExp(`@(${escaped.join('|')})`, 'g');
  const out = [];
  let last = 0, m, key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(<span key={key++} className="font-bold text-navy bg-navy/5 rounded px-0.5">@{m[1]}</span>);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}
